import React, { useState } from 'react';
import { AlertCircle, MoreHorizontal, Pencil, Plus, Tag as TagIcon, Trash2, X } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import {
  type CreateTagDefinitionRequest,
  type TagDefinition,
  type UpdateTagDefinitionRequest,
  useCreateTagDefinition,
  useDeleteTagDefinition,
  useTagDefinitions,
  useUpdateTagDefinition,
} from '../../hooks/useTagDefinitions';
import {
  type CreateTagValueRequest,
  type TagValue,
  type UpdateTagValueRequest,
  useCreateTagValue,
  useDeleteTagValue,
  useTagValuesByDefinition,
  useUpdateTagValue,
} from '../../hooks/useTagValues';
import ConfirmationModal from '../../components/ConfirmationModal';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AppSheet } from '@/components/ui/app-sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { EmptyState } from '@/components/ui/empty-state';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PageContainer } from '@/components/ui/page-container';
import { PageHeader } from '@/components/ui/page-header';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

const VALUE_TYPES = [
  { value: 'enumerated', label: 'Enumerated' },
  { value: 'freeform', label: 'Freeform' },
  { value: 'boolean', label: 'Boolean' },
];

const CARDINALITY_OPTIONS = [
  { value: 'single', label: 'Single' },
  { value: 'multi', label: 'Multiple' },
];

const RESOURCE_TYPES = [
  { value: 'device', label: 'Device' },
  { value: 'gateway', label: 'Gateway' },
  { value: 'region', label: 'Region' },
  { value: 'division', label: 'Division' },
  { value: 'credential', label: 'Credential' },
  { value: 'alert_definition', label: 'Alert Definition' },
  { value: 'change_management', label: 'Change Management' },
  { value: 'network', label: 'Network' },
  { value: 'interface', label: 'Interface' },
];

export default function TagDefinitions() {
  const { user } = useAuth();
  const organizationId = user?.organizationId;

  const [isCreateDefModalActive, setIsCreateDefModalActive] = useState(false);
  const [isEditDefModalActive, setIsEditDefModalActive] = useState(false);
  const [editingDefinition, setEditingDefinition] = useState<TagDefinition | null>(null);
  const [isDeleteDefConfirmActive, setIsDeleteDefConfirmActive] = useState(false);
  const [definitionToDelete, setDefinitionToDelete] = useState<TagDefinition | null>(null);

  const [selectedDefinitionId, setSelectedDefinitionId] = useState<string | null>(null);
  const [isCreateValueModalActive, setIsCreateValueModalActive] = useState(false);
  const [isEditValueModalActive, setIsEditValueModalActive] = useState(false);
  const [editingValue, setEditingValue] = useState<TagValue | null>(null);
  const [isDeleteValueConfirmActive, setIsDeleteValueConfirmActive] = useState(false);
  const [valueToDelete, setValueToDelete] = useState<TagValue | null>(null);

  const [formKey, setFormKey] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formValueType, setFormValueType] = useState('enumerated');
  const [formCardinality, setFormCardinality] = useState('single');
  const [formIsRequired, setFormIsRequired] = useState(false);
  const [formAllowDivisionValues, setFormAllowDivisionValues] = useState(true);
  const [formValidFor, setFormValidFor] = useState<string[]>(['device']);
  const [formColor, setFormColor] = useState('');
  const [formIcon, setFormIcon] = useState('');

  const [editFormDescription, setEditFormDescription] = useState('');
  const [editFormCardinality, setEditFormCardinality] = useState('single');
  const [editFormIsRequired, setEditFormIsRequired] = useState(false);
  const [editFormAllowDivisionValues, setEditFormAllowDivisionValues] = useState(true);
  const [editFormValidFor, setEditFormValidFor] = useState<string[]>(['device']);
  const [editFormColor, setEditFormColor] = useState('');
  const [editFormIcon, setEditFormIcon] = useState('');

  const [formValue, setFormValue] = useState('');
  const [formDisplayName, setFormDisplayName] = useState('');
  const [formValueDescription, setFormValueDescription] = useState('');
  const [formValueColor, setFormValueColor] = useState('');
  const [formSortOrder, setFormSortOrder] = useState(0);
  const [formIsDefault, setFormIsDefault] = useState(false);
  const [formIsActive, setFormIsActive] = useState(true);

  const [editFormDisplayName, setEditFormDisplayName] = useState('');
  const [editFormValueDescription, setEditFormValueDescription] = useState('');
  const [editFormValueColor, setEditFormValueColor] = useState('');
  const [editFormSortOrder, setEditFormSortOrder] = useState(0);
  const [editFormIsDefault, setEditFormIsDefault] = useState(false);
  const [editFormIsActive, setEditFormIsActive] = useState(true);

  const [submitError, setSubmitError] = useState<string | null>(null);

  const { data: tagDefinitions, isLoading, isError, error } = useTagDefinitions(organizationId);
  const { data: tagValues } = useTagValuesByDefinition(organizationId, selectedDefinitionId);

  const createDefinitionMutation = useCreateTagDefinition();
  const updateDefinitionMutation = useUpdateTagDefinition();
  const deleteDefinitionMutation = useDeleteTagDefinition();

  const createValueMutation = useCreateTagValue();
  const updateValueMutation = useUpdateTagValue();
  const deleteValueMutation = useDeleteTagValue();

  const resetDefForm = () => {
    setFormKey('');
    setFormDescription('');
    setFormValueType('enumerated');
    setFormCardinality('single');
    setFormIsRequired(false);
    setFormAllowDivisionValues(true);
    setFormValidFor(['device']);
    setFormColor('');
    setFormIcon('');
  };

  const resetEditDefForm = () => {
    setEditFormDescription('');
    setEditFormCardinality('single');
    setEditFormIsRequired(false);
    setEditFormAllowDivisionValues(true);
    setEditFormValidFor(['device']);
    setEditFormColor('');
    setEditFormIcon('');
  };

  const resetValueForm = () => {
    setFormValue('');
    setFormDisplayName('');
    setFormValueDescription('');
    setFormValueColor('');
    setFormSortOrder(0);
    setFormIsDefault(false);
    setFormIsActive(true);
  };

  const resetEditValueForm = () => {
    setEditFormDisplayName('');
    setEditFormValueDescription('');
    setEditFormValueColor('');
    setEditFormSortOrder(0);
    setEditFormIsDefault(false);
    setEditFormIsActive(true);
  };

  const handleCreateDefinition = (e: React.SubmitEvent) => {
    e.preventDefault();
    setSubmitError(null);
    const request: CreateTagDefinitionRequest = {
      key: formKey,
      description: formDescription || null,
      valueType: formValueType,
      cardinality: formCardinality,
      isRequired: formIsRequired,
      allowDivisionValues: formAllowDivisionValues,
      validFor: formValidFor,
      color: formColor || null,
      icon: formIcon || null,
    };
    createDefinitionMutation.mutate(request, {
      onSuccess: () => {
        setIsCreateDefModalActive(false);
        resetDefForm();
      },
      onError: (err) => {
        setSubmitError(
          err.response?.data.description ||
            err.response?.data.error ||
            'Failed to create tag definition.',
        );
      },
    });
  };

  const handleUpdateDefinition = (e: React.SubmitEvent) => {
    e.preventDefault();
    if (!editingDefinition) return;
    setSubmitError(null);
    const request: UpdateTagDefinitionRequest = {
      description: editFormDescription || null,
      cardinality: editFormCardinality,
      isRequired: editFormIsRequired,
      allowDivisionValues: editFormAllowDivisionValues,
      validFor: editFormValidFor,
      color: editFormColor || null,
      icon: editFormIcon || null,
    };
    updateDefinitionMutation.mutate(
      { id: editingDefinition.id, data: request },
      {
        onSuccess: () => {
          setIsEditDefModalActive(false);
          setEditingDefinition(null);
          resetEditDefForm();
        },
        onError: (err) => {
          setSubmitError(
            err.response?.data.description ||
              err.response?.data.error ||
              'Failed to update tag definition.',
          );
        },
      },
    );
  };

  const handleDeleteDefinition = (definition: TagDefinition) => {
    setDefinitionToDelete(definition);
    setIsDeleteDefConfirmActive(true);
  };

  const confirmDeleteDefinition = () => {
    if (!definitionToDelete) return;
    deleteDefinitionMutation.mutate(definitionToDelete.id, {
      onSuccess: () => {
        setIsDeleteDefConfirmActive(false);
        setDefinitionToDelete(null);
        setSubmitError(null);
      },
      onError: (err) => {
        setSubmitError(
          err.response?.data.description ||
            err.response?.data.error ||
            'Failed to delete tag definition.',
        );
        setIsDeleteDefConfirmActive(false);
        setDefinitionToDelete(null);
      },
    });
  };

  const cancelDeleteDefinition = () => {
    setIsDeleteDefConfirmActive(false);
    setDefinitionToDelete(null);
  };

  const openCreateDefModal = () => {
    resetDefForm();
    setSubmitError(null);
    setIsCreateDefModalActive(true);
  };

  const openEditDefModal = (definition: TagDefinition) => {
    setEditingDefinition(definition);
    setEditFormDescription(definition.description || '');
    setEditFormCardinality(definition.cardinality);
    setEditFormIsRequired(definition.isRequired);
    setEditFormAllowDivisionValues(definition.allowDivisionValues);
    setEditFormValidFor(definition.validFor);
    setEditFormColor(definition.color || '');
    setEditFormIcon(definition.icon || '');
    setSubmitError(null);
    setIsEditDefModalActive(true);
  };

  const handleCreateValue = (e: React.SubmitEvent) => {
    e.preventDefault();
    if (!selectedDefinitionId) return;
    setSubmitError(null);
    const request: CreateTagValueRequest = {
      value: formValue,
      displayName: formDisplayName || null,
      description: formValueDescription || null,
      color: formValueColor || null,
      sortOrder: formSortOrder,
      isDefault: formIsDefault,
      isActive: formIsActive,
    };
    createValueMutation.mutate(
      { tagDefinitionId: selectedDefinitionId, data: request },
      {
        onSuccess: () => {
          setIsCreateValueModalActive(false);
          resetValueForm();
        },
        onError: (err) => {
          setSubmitError(
            err.response?.data.description ||
              err.response?.data.error ||
              'Failed to create tag value.',
          );
        },
      },
    );
  };

  const handleUpdateValue = (e: React.SubmitEvent) => {
    e.preventDefault();
    if (!editingValue) return;
    setSubmitError(null);
    const request: UpdateTagValueRequest = {
      displayName: editFormDisplayName || null,
      description: editFormValueDescription || null,
      color: editFormValueColor || null,
      sortOrder: editFormSortOrder,
      isDefault: editFormIsDefault,
      isActive: editFormIsActive,
    };
    updateValueMutation.mutate(
      { id: editingValue.id, data: request },
      {
        onSuccess: () => {
          setIsEditValueModalActive(false);
          setEditingValue(null);
          resetEditValueForm();
        },
        onError: (err) => {
          setSubmitError(
            err.response?.data.description ||
              err.response?.data.error ||
              'Failed to update tag value.',
          );
        },
      },
    );
  };

  const handleDeleteValue = (value: TagValue) => {
    setValueToDelete(value);
    setIsDeleteValueConfirmActive(true);
  };

  const confirmDeleteValue = () => {
    if (!valueToDelete) return;
    deleteValueMutation.mutate(valueToDelete.id, {
      onSuccess: () => {
        setIsDeleteValueConfirmActive(false);
        setValueToDelete(null);
        setSubmitError(null);
      },
      onError: (err) => {
        setSubmitError(
          err.response?.data.description ||
            err.response?.data.error ||
            'Failed to delete tag value.',
        );
        setIsDeleteValueConfirmActive(false);
        setValueToDelete(null);
      },
    });
  };

  const cancelDeleteValue = () => {
    setIsDeleteValueConfirmActive(false);
    setValueToDelete(null);
  };

  const openCreateValueModal = (definitionId: string) => {
    setSelectedDefinitionId(definitionId);
    resetValueForm();
    setSubmitError(null);
    setIsCreateValueModalActive(true);
  };

  const openEditValueModal = (value: TagValue) => {
    setEditingValue(value);
    setEditFormDisplayName(value.displayName || '');
    setEditFormValueDescription(value.description || '');
    setEditFormValueColor(value.color || '');
    setEditFormSortOrder(value.sortOrder);
    setEditFormIsDefault(value.isDefault);
    setEditFormIsActive(value.isActive);
    setSubmitError(null);
    setIsEditValueModalActive(true);
  };

  const handleResourceTypeToggle = (resourceType: string) => {
    setFormValidFor((prev) =>
      prev.includes(resourceType)
        ? prev.filter((t) => t !== resourceType)
        : [...prev, resourceType],
    );
  };

  const handleEditResourceTypeToggle = (resourceType: string) => {
    setEditFormValidFor((prev) =>
      prev.includes(resourceType)
        ? prev.filter((t) => t !== resourceType)
        : [...prev, resourceType],
    );
  };

  if (isLoading) {
    return (
      <PageContainer>
        <PageHeader title="Tag definitions" />
        <p className="text-sm text-muted-foreground">Loading tag definitions…</p>
      </PageContainer>
    );
  }

  if (isError) {
    return (
      <PageContainer>
        <PageHeader title="Tag definitions" />
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Error loading tag definitions: {error.message}
          </AlertDescription>
        </Alert>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="Tag definitions"
        primary={
          <Button onClick={openCreateDefModal} className="gap-1.5">
            <Plus className="w-4 h-4" />
            Add tag definition
          </Button>
        }
      />

      {submitError ? (
        <Alert variant="destructive" className="mb-4 relative">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{submitError}</AlertDescription>
          <button
            type="button"
            onClick={() => { setSubmitError(null); }}
            className="absolute top-2 right-2 inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-accent"
            aria-label="Dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </Alert>
      ) : null}

      {tagDefinitions && tagDefinitions.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border-subtle">
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-3 py-2 font-medium">Key</th>
                <th className="px-3 py-2 font-medium">Description</th>
                <th className="px-3 py-2 font-medium">Value type</th>
                <th className="px-3 py-2 font-medium">Cardinality</th>
                <th className="px-3 py-2 font-medium">Valid for</th>
                <th className="px-3 py-2 font-medium">Required</th>
                <th className="px-3 py-2 font-medium w-32"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle">
              {tagDefinitions.map((definition) => (
                <React.Fragment key={definition.id}>
                  <tr className="hover:bg-accent/30">
                    <td className="px-3 py-2">
                      <span className="font-semibold">{definition.key}</span>
                      {definition.isBuiltIn ? (
                        <Badge className="ml-2 bg-sev-5/15 text-sev-5 border-0 font-normal text-[10px]">
                          Built-in
                        </Badge>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {definition.description || '—'}
                    </td>
                    <td className="px-3 py-2">
                      <Badge variant="secondary" className="font-normal capitalize">
                        {definition.valueType}
                      </Badge>
                    </td>
                    <td className="px-3 py-2">
                      <Badge variant="secondary" className="font-normal capitalize">
                        {definition.cardinality}
                      </Badge>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-1">
                        {definition.validFor.map((type) => (
                          <Badge
                            key={type}
                            variant="outline"
                            className="text-[10px] font-normal capitalize"
                          >
                            {type.replace(/_/g, ' ')}
                          </Badge>
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {definition.isRequired ? 'Yes' : 'No'}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1.5 justify-end">
                        {definition.valueType === 'enumerated' ? (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7"
                            onClick={() => {
                              setSelectedDefinitionId(
                                selectedDefinitionId === definition.id ? null : definition.id,
                              );
                            }}
                          >
                            {selectedDefinitionId === definition.id ? 'Hide values' : 'View values'}
                          </Button>
                        ) : null}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={`Actions for ${definition.key}`}
                            >
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => { openEditDefModal(definition); }}
                              disabled={definition.isBuiltIn}
                              className="gap-2"
                            >
                              <Pencil className="w-4 h-4" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => { handleDeleteDefinition(definition); }}
                              disabled={definition.isBuiltIn}
                              className="gap-2 text-sev-1 focus:text-sev-1"
                            >
                              <Trash2 className="w-4 h-4" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </td>
                  </tr>
                  {selectedDefinitionId === definition.id ? (
                    <tr>
                      <td colSpan={7} className="bg-muted/30 px-6 py-4">
                        <div className="flex items-center justify-between mb-3">
                          <h5 className="text-sm font-semibold">
                            Tag values for &quot;{definition.key}&quot;
                          </h5>
                          <Button
                            size="sm"
                            onClick={() => { openCreateValueModal(definition.id); }}
                            className="gap-1.5"
                          >
                            <Plus className="w-3.5 h-3.5" />
                            Add value
                          </Button>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead className="border-b border-border-subtle">
                              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                                <th className="px-3 py-2 font-medium">Value</th>
                                <th className="px-3 py-2 font-medium">Display name</th>
                                <th className="px-3 py-2 font-medium">Description</th>
                                <th className="px-3 py-2 font-medium">Sort order</th>
                                <th className="px-3 py-2 font-medium">Default</th>
                                <th className="px-3 py-2 font-medium">Active</th>
                                <th className="px-3 py-2 font-medium w-12"></th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-border-subtle">
                              {tagValues && tagValues.length > 0 ? (
                                tagValues.map((value) => (
                                  <tr key={value.id}>
                                    <td className="px-3 py-2">
                                      <span className="font-semibold">{value.value}</span>
                                      {value.color ? (
                                        <span
                                          className="ml-2 inline-block w-3 h-3 rounded-full align-middle"
                                          style={{ backgroundColor: value.color }}
                                        />
                                      ) : null}
                                    </td>
                                    <td className="px-3 py-2 text-xs">
                                      {value.displayName || '—'}
                                    </td>
                                    <td className="px-3 py-2 text-xs text-muted-foreground">
                                      {value.description || '—'}
                                    </td>
                                    <td className="px-3 py-2 text-xs">{value.sortOrder}</td>
                                    <td className="px-3 py-2 text-xs">
                                      {value.isDefault ? 'Yes' : 'No'}
                                    </td>
                                    <td className="px-3 py-2">
                                      <Badge
                                        className={
                                          value.isActive
                                            ? 'bg-sev-4/15 text-sev-4 border-0 font-normal'
                                            : 'bg-sev-1/15 text-sev-1 border-0 font-normal'
                                        }
                                      >
                                        {value.isActive ? 'Active' : 'Inactive'}
                                      </Badge>
                                    </td>
                                    <td className="px-3 py-2">
                                      <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            aria-label={`Actions for ${value.value}`}
                                          >
                                            <MoreHorizontal className="w-4 h-4" />
                                          </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end">
                                          <DropdownMenuItem
                                            onClick={() => { openEditValueModal(value); }}
                                            className="gap-2"
                                          >
                                            <Pencil className="w-4 h-4" />
                                            Edit
                                          </DropdownMenuItem>
                                          <DropdownMenuItem
                                            onClick={() => { handleDeleteValue(value); }}
                                            className="gap-2 text-sev-1 focus:text-sev-1"
                                          >
                                            <Trash2 className="w-4 h-4" />
                                            Delete
                                          </DropdownMenuItem>
                                        </DropdownMenuContent>
                                      </DropdownMenu>
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td
                                    colSpan={7}
                                    className="text-center text-xs text-muted-foreground py-3"
                                  >
                                    No values defined yet.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  ) : null}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyState
          variant="no-data"
          icon={TagIcon}
          title="No tag definitions yet"
          description="Tag definitions let you label resources (devices, regions, alerts) with shared categories."
          action={
            <Button onClick={openCreateDefModal} className="gap-1.5">
              <Plus className="w-4 h-4" />
              Add tag definition
            </Button>
          }
        />
      )}

      <AppSheet
        open={isCreateDefModalActive}
        onOpenChange={(open) => { if (!open) setIsCreateDefModalActive(false); }}
        title="Create new tag definition"
        size="md"
      >
        <AppSheet.Body>
          <form id="create-tag-def-form" onSubmit={handleCreateDefinition} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="td-key">Key *</Label>
              <Input
                id="td-key"
                type="text"
                placeholder="e.g., environment, priority, status"
                value={formKey}
                onChange={(e) => { setFormKey(e.target.value); }}
                required
                className="font-mono"
              />
              <p className="text-xs text-muted-foreground">
                A unique identifier for this tag (lowercase, no spaces).
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="td-desc">Description</Label>
              <Textarea
                id="td-desc"
                placeholder="Optional description of this tag"
                value={formDescription}
                onChange={(e) => { setFormDescription(e.target.value); }}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="td-vtype">Value type *</Label>
              <Select value={formValueType} onValueChange={setFormValueType}>
                <SelectTrigger id="td-vtype">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {VALUE_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="td-card">Cardinality *</Label>
              <Select value={formCardinality} onValueChange={setFormCardinality}>
                <SelectTrigger id="td-card">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CARDINALITY_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Single = one value per resource, Multiple = many values per resource.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label>Valid for *</Label>
              <div className="grid grid-cols-2 gap-2">
                {RESOURCE_TYPES.map((t) => (
                  <label
                    key={t.value}
                    className="flex items-center gap-2 text-sm cursor-pointer"
                  >
                    <Checkbox
                      checked={formValidFor.includes(t.value)}
                      onCheckedChange={() => { handleResourceTypeToggle(t.value); }}
                    />
                    {t.label}
                  </label>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Which resource types can be tagged with this definition.
              </p>
            </div>

            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={formIsRequired}
                onCheckedChange={(v) => { setFormIsRequired(v === true); }}
              />
              Required
            </label>

            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={formAllowDivisionValues}
                onCheckedChange={(v) => { setFormAllowDivisionValues(v === true); }}
              />
              Allow division-specific values
            </label>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="td-color">Color (optional)</Label>
                <Input
                  id="td-color"
                  type="text"
                  placeholder="#3298dc"
                  value={formColor}
                  onChange={(e) => { setFormColor(e.target.value); }}
                  className="font-mono"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="td-icon">Icon (optional)</Label>
                <Input
                  id="td-icon"
                  type="text"
                  placeholder="tag, bookmark, star"
                  value={formIcon}
                  onChange={(e) => { setFormIcon(e.target.value); }}
                />
              </div>
            </div>

            {submitError ? (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{submitError}</AlertDescription>
              </Alert>
            ) : null}
          </form>
        </AppSheet.Body>
        <AppSheet.Footer>
          <Button
            variant="outline"
            type="button"
            onClick={() => { setIsCreateDefModalActive(false); }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="create-tag-def-form"
            disabled={createDefinitionMutation.isPending}
          >
            {createDefinitionMutation.isPending ? 'Saving…' : 'Create tag definition'}
          </Button>
        </AppSheet.Footer>
      </AppSheet>

      <AppSheet
        open={isEditDefModalActive}
        onOpenChange={(open) => { if (!open) setIsEditDefModalActive(false); }}
        title={`Edit tag definition: ${editingDefinition?.key ?? ''}`}
        size="md"
      >
        <AppSheet.Body>
          <form id="edit-tag-def-form" onSubmit={handleUpdateDefinition} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="ed-td-desc">Description</Label>
              <Textarea
                id="ed-td-desc"
                placeholder="Optional description of this tag"
                value={editFormDescription}
                onChange={(e) => { setEditFormDescription(e.target.value); }}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="ed-td-card">Cardinality</Label>
              <Select value={editFormCardinality} onValueChange={setEditFormCardinality}>
                <SelectTrigger id="ed-td-card">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CARDINALITY_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label>Valid for</Label>
              <div className="grid grid-cols-2 gap-2">
                {RESOURCE_TYPES.map((t) => (
                  <label
                    key={t.value}
                    className="flex items-center gap-2 text-sm cursor-pointer"
                  >
                    <Checkbox
                      checked={editFormValidFor.includes(t.value)}
                      onCheckedChange={() => { handleEditResourceTypeToggle(t.value); }}
                    />
                    {t.label}
                  </label>
                ))}
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={editFormIsRequired}
                onCheckedChange={(v) => { setEditFormIsRequired(v === true); }}
              />
              Required
            </label>

            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={editFormAllowDivisionValues}
                onCheckedChange={(v) => { setEditFormAllowDivisionValues(v === true); }}
              />
              Allow division-specific values
            </label>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="ed-td-color">Color (optional)</Label>
                <Input
                  id="ed-td-color"
                  type="text"
                  placeholder="#3298dc"
                  value={editFormColor}
                  onChange={(e) => { setEditFormColor(e.target.value); }}
                  className="font-mono"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ed-td-icon">Icon (optional)</Label>
                <Input
                  id="ed-td-icon"
                  type="text"
                  placeholder="tag, bookmark, star"
                  value={editFormIcon}
                  onChange={(e) => { setEditFormIcon(e.target.value); }}
                />
              </div>
            </div>

            {submitError ? (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{submitError}</AlertDescription>
              </Alert>
            ) : null}
          </form>
        </AppSheet.Body>
        <AppSheet.Footer>
          <Button
            variant="outline"
            type="button"
            onClick={() => { setIsEditDefModalActive(false); }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="edit-tag-def-form"
            disabled={updateDefinitionMutation.isPending}
          >
            {updateDefinitionMutation.isPending ? 'Saving…' : 'Update tag definition'}
          </Button>
        </AppSheet.Footer>
      </AppSheet>

      <AppSheet
        open={isCreateValueModalActive}
        onOpenChange={(open) => { if (!open) setIsCreateValueModalActive(false); }}
        title="Create new tag value"
        size="md"
      >
        <AppSheet.Body>
          <form id="create-tag-value-form" onSubmit={handleCreateValue} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="tv-value">Value *</Label>
              <Input
                id="tv-value"
                type="text"
                placeholder="e.g., production, development, high, low"
                value={formValue}
                onChange={(e) => { setFormValue(e.target.value); }}
                required
                className="font-mono"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="tv-display">Display name</Label>
              <Input
                id="tv-display"
                type="text"
                placeholder="Optional friendly name"
                value={formDisplayName}
                onChange={(e) => { setFormDisplayName(e.target.value); }}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="tv-desc">Description</Label>
              <Textarea
                id="tv-desc"
                placeholder="Optional description"
                value={formValueDescription}
                onChange={(e) => { setFormValueDescription(e.target.value); }}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="tv-color">Color</Label>
                <Input
                  id="tv-color"
                  type="text"
                  placeholder="#3298dc"
                  value={formValueColor}
                  onChange={(e) => { setFormValueColor(e.target.value); }}
                  className="font-mono"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="tv-sort">Sort order</Label>
                <Input
                  id="tv-sort"
                  type="number"
                  value={formSortOrder}
                  onChange={(e) => { setFormSortOrder(parseInt(e.target.value, 10) || 0); }}
                  className="font-mono"
                />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={formIsDefault}
                onCheckedChange={(v) => { setFormIsDefault(v === true); }}
              />
              Default value
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={formIsActive}
                onCheckedChange={(v) => { setFormIsActive(v === true); }}
              />
              Active
            </label>
            {submitError ? (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{submitError}</AlertDescription>
              </Alert>
            ) : null}
          </form>
        </AppSheet.Body>
        <AppSheet.Footer>
          <Button
            variant="outline"
            type="button"
            onClick={() => { setIsCreateValueModalActive(false); }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="create-tag-value-form"
            disabled={createValueMutation.isPending}
          >
            {createValueMutation.isPending ? 'Saving…' : 'Create value'}
          </Button>
        </AppSheet.Footer>
      </AppSheet>

      <AppSheet
        open={isEditValueModalActive}
        onOpenChange={(open) => { if (!open) setIsEditValueModalActive(false); }}
        title={`Edit tag value: ${editingValue?.value ?? ''}`}
        size="md"
      >
        <AppSheet.Body>
          <form id="edit-tag-value-form" onSubmit={handleUpdateValue} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="ed-tv-display">Display name</Label>
              <Input
                id="ed-tv-display"
                type="text"
                placeholder="Optional friendly name"
                value={editFormDisplayName}
                onChange={(e) => { setEditFormDisplayName(e.target.value); }}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ed-tv-desc">Description</Label>
              <Textarea
                id="ed-tv-desc"
                placeholder="Optional description"
                value={editFormValueDescription}
                onChange={(e) => { setEditFormValueDescription(e.target.value); }}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="ed-tv-color">Color</Label>
                <Input
                  id="ed-tv-color"
                  type="text"
                  placeholder="#3298dc"
                  value={editFormValueColor}
                  onChange={(e) => { setEditFormValueColor(e.target.value); }}
                  className="font-mono"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ed-tv-sort">Sort order</Label>
                <Input
                  id="ed-tv-sort"
                  type="number"
                  value={editFormSortOrder}
                  onChange={(e) => {
                    setEditFormSortOrder(parseInt(e.target.value, 10) || 0);
                  }}
                  className="font-mono"
                />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={editFormIsDefault}
                onCheckedChange={(v) => { setEditFormIsDefault(v === true); }}
              />
              Default value
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={editFormIsActive}
                onCheckedChange={(v) => { setEditFormIsActive(v === true); }}
              />
              Active
            </label>
            {submitError ? (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{submitError}</AlertDescription>
              </Alert>
            ) : null}
          </form>
        </AppSheet.Body>
        <AppSheet.Footer>
          <Button
            variant="outline"
            type="button"
            onClick={() => { setIsEditValueModalActive(false); }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="edit-tag-value-form"
            disabled={updateValueMutation.isPending}
          >
            {updateValueMutation.isPending ? 'Saving…' : 'Update value'}
          </Button>
        </AppSheet.Footer>
      </AppSheet>

      <ConfirmationModal
        isActive={isDeleteDefConfirmActive}
        title="Delete tag definition"
        message={`Are you sure you want to delete the tag definition "${definitionToDelete?.key || 'Unknown'}"? This will also delete all associated tag values and remove all tags using this definition from resources.`}
        confirmText="Delete tag definition"
        cancelText="Cancel"
        onConfirm={confirmDeleteDefinition}
        onCancel={cancelDeleteDefinition}
        isLoading={deleteDefinitionMutation.isPending}
        isDanger={true}
      />

      <ConfirmationModal
        isActive={isDeleteValueConfirmActive}
        title="Delete tag value"
        message={`Are you sure you want to delete the tag value "${valueToDelete?.value || 'Unknown'}"? Resources currently using this value will need to be updated.`}
        confirmText="Delete tag value"
        cancelText="Cancel"
        onConfirm={confirmDeleteValue}
        onCancel={cancelDeleteValue}
        isLoading={deleteValueMutation.isPending}
        isDanger={true}
      />
    </PageContainer>
  );
}
